import { useEffect, useState } from "react";
import { Stack, Divider, Typography, Box, TextField, CircularProgress } from "@material-ui/core";
import { useTranslation } from "react-i18next";
import { NoteAlt } from "@material-ui/icons";
import { ContactTableAiFieldFlag } from "./ContactTableAiFieldFlag";
import useContactWrites from "hooks/contacts/useContactWrites";
import { CONTACT_NOTES_MAX_LENGTH } from "utils/contacts/contactWriteLimits";
import {
  CONTACT_NOT_IN_YOUR_BOOK_MESSAGE,
  isContactNotInYourBookError
} from "utils/contacts/contactWriteErrors";
import type { RealEstateContact } from "../../../../../@types/sharedSchema";
import type { ChangeEvent, FormEvent } from "react";

type ContactTableAdditionalNotesProps = {
  contact: RealEstateContact;
  hideInputWhenEmpty?: boolean;
};

/**
 * The inline "additional notes" editor on the expanded contact row.
 *
 * WHY THE SAVE GOES THROUGH `useContactWrites().updateContact` — this component is rendered by
 * `ContactTableNestedRowGeneral`, i.e. by `ViewContactDialog`, and one of that dialog's
 * hosts is now the CRM side panel, whose agency view can put a row belonging to ANOTHER
 * AGENT on screen. The legacy tables' browser-direct writer (`useLegacyContactWrites`) writes
 * `dashboard-users-v2/{signed-in user}/contacts/{id}` with `set(..., { merge: true })`,
 * so on a colleague's contact it does not fail — it CREATES a document in the viewer's
 * own book holding nothing but this note. A phantom contact, with a note attached, that
 * nobody asked for and nothing later repairs.
 *
 * The server edit cannot create, and the "not in your book" error it raises is exactly
 * the condition worth telling the user about. The same
 * guard also covers the contact having been deleted from another tab or the mobile app
 * while this row was open.
 *
 * A note carries no CRM axis and no customer type, so the prior state (`contact`) is
 * only passed for the normalizers' benefit; it changes nothing about this write. It is
 * still passed rather than defaulted, because a caller that has to state the prior
 * version cannot accidentally derive one from an array that isn't loaded — which is the
 * other half of the same defect.
 */
export function ContactTableAdditionalNotes({
  contact,
  hideInputWhenEmpty
}: ContactTableAdditionalNotesProps): JSX.Element {
  const { t: translate } = useTranslation();

  const [additionalNotesForm, setAdditionalNotesForm] = useState("");
  const [loading, setLoading] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const { additionalNotes } = contact;

  const { updateContact } = useContactWrites();

  useEffect(() => {
    setAdditionalNotesForm(additionalNotes ?? "");
  }, [additionalNotes]);

  const handleBioSubmit = async (
    e: FormEvent<HTMLFormElement | HTMLInputElement | HTMLTextAreaElement>
  ): Promise<void> => {
    e.preventDefault();

    setLoading(true);

    const trimmedAdditionNotes = additionalNotesForm.trim();

    if (trimmedAdditionNotes === additionalNotes) {
      setLoading(false);
      setSaveError(null);
      setAdditionalNotesForm(trimmedAdditionNotes);
      return;
    }

    try {
      await updateContact(contact.id, { additionalNotes: trimmedAdditionNotes });
      setSaveError(null);
    } catch (error) {
      // Put the field back to what is actually stored — leaving the unsaved text in
      // place reads as a successful save.
      setAdditionalNotesForm(additionalNotes ?? "");
      // Inline rather than a snackbar: this component has eight hosts and no snackbar
      // provider of its own, and the message belongs next to the field the user typed
      // in. The two cases are genuinely different advice, so they are worded apart.
      setSaveError(
        isContactNotInYourBookError(error)
          ? CONTACT_NOT_IN_YOUR_BOOK_MESSAGE
          : "שמירת ההערה נכשלה. נסו שוב."
      );
    } finally {
      setLoading(false);
    }
  };

  const handleBioChange = ({ target: { value } }: ChangeEvent<HTMLInputElement>): void => {
    setAdditionalNotesForm(value);
    if (saveError) setSaveError(null);
  };

  return (
    <>
      <Divider />
      <Stack gap={2}>
        <ContactTableAiFieldFlag id="additionalNotes" contact={contact}>
          <Stack direction="row" alignItems="center" gap={1}>
            <NoteAlt color="info" />
            <Typography variant="h6">
              {translate("contactsPage.tableColumns.additionalNotes")}
            </Typography>
          </Stack>
        </ContactTableAiFieldFlag>
        {!(hideInputWhenEmpty && !additionalNotes) && (
          <Box component="form" onSubmit={handleBioSubmit}>
            <TextField
              fullWidth
              multiline
              value={additionalNotesForm}
              disabled={loading}
              error={Boolean(saveError)}
              helperText={saveError ?? undefined}
              placeholder={translate("contactsPage.tableColumns.additionalNotes")}
              // `maxLength` mirrors the server's cap — this field now saves through
              // POST /contacts/update, which rejects an over-long note instead of
              // truncating it, and it does so only after the text has been typed.
              inputProps={{ sx: { resize: "none", overflow: "hidden" }, maxLength: CONTACT_NOTES_MAX_LENGTH }}
              InputProps={{
                endAdornment: loading && <CircularProgress size={20} />
              }}
              onChange={handleBioChange}
              onBlur={loading ? undefined : handleBioSubmit}
            />
          </Box>
        )}
      </Stack>
    </>
  );
}
